/* What's in the Box. Lives in an asset (not an inline snippet script) because
   Safari/WebKit does not execute <script> tags that are descendants of a custom
   element — the corporate store wraps this list in <corporate-gifts>, so an
   inline definition never ran there and the list stayed hidden. */
(function () {
  if (customElements.get('bundle-contents')) return;

  const STOREFRONT_API_VERSION = '2025-01';
  const API_TIMEOUT_MS = 8000;
  const IMAGE_TIMEOUT_MS = 4000;

  // A corporate gift variant is a SKU duplicate of the physical box
  // (WGP_FREN_WG_BOX_CORP → WGP_FREN_WG_BOX), and box contents are only
  // stored against the physical SKU. Mirrors physicalSkuForCorporate in
  // the API (corporate/service.js).
  const CORPORATE_SKU_SUFFIX = '_CORP';

  function physicalSku(sku) {
    const s = typeof sku === 'string' ? sku.trim() : '';
    if (s.length <= CORPORATE_SKU_SUFFIX.length) return s;
    if (!s.toUpperCase().endsWith(CORPORATE_SKU_SUFFIX)) return s;
    return s.slice(0, -CORPORATE_SKU_SUFFIX.length);
  }

  function buildVariantNodesQuery(variantIds) {
    const fragments = variantIds.map(
      (id, i) => `v${i}: node(id: "gid://shopify/ProductVariant/${id}") {
        ... on ProductVariant {
          id
          title
          image { url altText }
          product { title handle }
        }
      }`
    );
    return `query VariantImages { ${fragments.join('\n')} }`;
  }

  function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(function () {
      controller.abort();
    }, timeoutMs);
    const opts = Object.assign({ credentials: 'omit', signal: controller.signal }, options);
    return fetch(url, opts).finally(function () {
      clearTimeout(timer);
    });
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) {
      el.setAttribute('hidden', '');
      el.hidden = true;
    } else {
      el.hidden = false;
      el.removeAttribute('hidden');
    }
  }

  class BundleContents extends HTMLElement {
    constructor() {
      super();
      this._cache = {};
    }

    connectedCallback() {
      this.listEl = this.querySelector('.bundle-contents__list');
      this.token = this.dataset.storefrontToken;
      this.shopDomain = this.dataset.shopDomain;
      this.wildgrainApiUrl = this.dataset.apiUrl;
      this.storefrontUrl = 'https://' + this.shopDomain + '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
      this.currentSku = this.dataset.variantSku || '';
      this.expandPacks = this.hasAttribute('data-expand-packs');

      // Optional restyled product shots (api styled-thumbnails, e.g. "minimal")
      // keyed by handle, used in place of each item's own photo once loaded.
      // (The shared list re-connects every time it moves between modals, so
      // the map must survive re-connection — only the first connect fetches.)
      this.thumbStyle = (this.dataset.thumbStyle || '').trim().toLowerCase();
      if (this.styledByHandle === undefined) this.styledByHandle = null;
      if (this.thumbStyle && this.wildgrainApiUrl && !this._styledPromise) {
        this._styledPromise = this.loadStyledThumbs().then((map) => {
          this.styledByHandle = map;
          if (map && this._lastRendered) this.render(this._lastRendered);
        });
      }

      // A deferred list is driven by a host that reuses one element across
      // many products (the corporate store), which needs stricter handling
      // than a PDP list that only ever describes its own product.
      this.hostDriven = this.hasAttribute('data-defer-load');

      // A shared list gets moved between hosts (the corporate store keeps one
      // element for every product modal), so re-connecting must not rebind.
      if (this._bound) return;
      this._bound = true;

      // Deferred lists are driven by their host, which knows which variant the
      // shopper is looking at and when the list becomes visible.
      if (!this.hasAttribute('data-defer-load')) {
        this.load(this.dataset.variantId, this.currentSku);

        document.addEventListener('variant:changed', (e) => {
          const variant = e.detail && e.detail.variant;
          if (variant && variant.id) {
            this.load(String(variant.id), variant.sku || this.currentSku);
          }
        });
      }

      const handleItemClick = (e) => {
        const item = e.target.closest('.bundle-contents__item');
        if (!item) return;
        const handle = item.dataset.handle;
        if (!handle) return;
        this.openQuickView('/products/' + handle);
      };

      if (this.listEl) {
        this.listEl.addEventListener('click', handleItemClick);
        this.listEl.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleItemClick(e);
          }
        });
      }
    }

    openQuickView(productUrl) {
      const modal = document.getElementById('BundleQuickView');
      if (!modal) return;
      modal.dataset.productUrl = productUrl;
      if (typeof modal.show === 'function') {
        modal.show();
      }
    }

    setVisible(visible) {
      setHidden(this, !visible);
      const slot = this.parentElement;
      if (slot && slot.hasAttribute('data-cg-bundle-slot')) {
        setHidden(slot, !visible);
      }
    }

    async load(numericId, sku) {
      const cacheKey = sku || numericId;
      if (!this.listEl) this.listEl = this.querySelector('.bundle-contents__list');

      // A host-driven list can jump to a different product entirely, so blank
      // it up front rather than let another box's contents linger. A PDP list
      // keeps the previous variant on screen to avoid flashing empty.
      if (this._requestedKey !== cacheKey) {
        this._requestedKey = cacheKey;
        if (this.hostDriven) {
          this.setVisible(false);
          if (this.listEl) this.listEl.innerHTML = '';
        }
      }

      if (this._cache[cacheKey]) {
        this.render(this._cache[cacheKey]);
        return;
      }

      if (!sku || !this.wildgrainApiUrl) {
        console.warn('[bundle] no sku to look up', { sku: sku, api: this.wildgrainApiUrl });
      }

      if (sku && this.wildgrainApiUrl) {
        const dbResult = await this.loadFromApi(sku);
        if (this._requestedKey !== cacheKey) return;
        if (dbResult && dbResult.length > 0) {
          this._cache[cacheKey] = dbResult;
          this.render(dbResult);
          this.hydrateImages(dbResult, cacheKey);
          return;
        }
      }

      this.setVisible(false);
    }

    // Same cache key as corporate-gifts.js so the store page fetches the map once.
    async loadStyledThumbs() {
      const key = 'cg-thumbs-' + this.thumbStyle;
      const now = Date.now();
      let json = null;
      try {
        const hit = JSON.parse(sessionStorage.getItem(key));
        if (hit && hit.ts && now - hit.ts < 10 * 60 * 1000) json = hit.data;
      } catch (e) {
        /* storage unavailable or legacy entry */
      }
      if (!json) {
        try {
          const res = await fetchWithTimeout(
            this.wildgrainApiUrl + '/api/styled-thumbnails/map/' + encodeURIComponent(this.thumbStyle),
            { method: 'GET' },
            API_TIMEOUT_MS
          );
          if (!res.ok) return null;
          json = await res.json();
          try {
            sessionStorage.setItem(key, JSON.stringify({ ts: now, data: json }));
          } catch (e) {
            /* quota / private mode */
          }
        } catch (e) {
          return null;
        }
      }
      const byHandle = {};
      Object.values((json && json.thumbnails) || {}).forEach((entry) => {
        if (entry && entry.url && entry.handle) byHandle[entry.handle] = entry.url;
      });
      return Object.keys(byHandle).length ? byHandle : null;
    }

    async loadFromApi(sku) {
      try {
        const lookupSku = physicalSku(sku);
        const res = await fetchWithTimeout(
          this.wildgrainApiUrl + '/api/bundle/storefront/' + encodeURIComponent(lookupSku) + '?box_number=1',
          { method: 'GET' },
          API_TIMEOUT_MS
        );
        if (!res.ok) return null;

        const json = await res.json();
        if (!json.success || !json.items || json.items.length === 0) return null;

        // Names and handles come from the API so the list can render even if Safari
        // blocks the follow-up Storefront GraphQL call used only for images.
        return json.items.map((item) => ({
          quantity: item.quantity,
          variantId: item.variant_shopify_id,
          productVariant: {
            title: item.product_name || item.sku,
            image: null,
            product: {
              title: item.product_name || item.sku,
              handle: item.handle || '',
            },
            selectedOptions: [],
          },
        }));
      } catch (error) {
        console.warn('[bundle] api lookup failed', error);
        return null;
      }
    }

    async hydrateImages(components, cacheKey) {
      const variantIds = components.map((c) => c.variantId).filter(Boolean);
      if (!variantIds.length || !this.token || !this.storefrontUrl) return;

      try {
        const gqlRes = await fetchWithTimeout(
          this.storefrontUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.token,
            },
            body: JSON.stringify({ query: buildVariantNodesQuery(variantIds) }),
          },
          IMAGE_TIMEOUT_MS
        );
        const gqlJson = await gqlRes.json();
        if (this._requestedKey !== cacheKey) return;
        if (!gqlJson || !gqlJson.data) return;

        const variantData = {};
        Object.values(gqlJson.data).forEach((node) => {
          if (node && node.id) {
            const numId = node.id.replace('gid://shopify/ProductVariant/', '');
            variantData[numId] = node;
          }
        });

        let changed = false;
        components.forEach((c) => {
          const vd = variantData[c.variantId];
          if (!vd) return;
          changed = true;
          c.productVariant.title = vd.title || c.productVariant.title;
          c.productVariant.image = vd.image || null;
          c.productVariant.product = {
            title: (vd.product && vd.product.title) || c.productVariant.product.title,
            handle: (vd.product && vd.product.handle) || '',
          };
        });

        if (!changed) return;
        this._cache[cacheKey] = components;
        this.render(components);
      } catch (error) {
        console.warn('[bundle] variant images unavailable', error);
      }
    }

    // "Do the math" for pack-based products: when expand_packs is on and a
    // product title ends in "(N-pack)", multiply quantity × N and drop the
    // suffix — e.g. 4 × "Croissants (4-pack)" → { qty: "16", name: "Croissants" }.
    // Titles ending in anything else (e.g. "8 oz") are left as "N × Title".
    formatRow(quantity, title) {
      const packMatch = this.expandPacks && title.match(/\s*\((\d+)[-\s]packs?\)\s*$/i);
      if (packMatch) {
        const total = quantity * parseInt(packMatch[1], 10);
        const name = title.slice(0, packMatch.index).trim();
        return { qty: `${total} &times;`, name };
      }
      return { qty: `${quantity} &times;`, name: title };
    }

    render(components) {
      if (!this.listEl) this.listEl = this.querySelector('.bundle-contents__list');
      if (!this.listEl) return;

      if (!components.length) {
        this.setVisible(false);
        return;
      }

      this._lastRendered = components;

      this.listEl.innerHTML = components
        .map((c) => {
          const pv = c.productVariant;
          const title = (pv.product && pv.product.title) || pv.title;
          const handle = (pv.product && pv.product.handle) || '';
          const styled = this.styledByHandle && handle ? this.styledByHandle[handle] : null;
          const imgUrl = styled || (pv.image && pv.image.url);
          const alt = (pv.image && pv.image.altText) || title;
          const opts = (pv.selectedOptions || [])
            .filter((o) => o.name !== 'Title' || o.value !== 'Default Title')
            .map((o) => o.value);
          const subtitle = opts.length ? opts.join(' / ') : '';
          const row = this.formatRow(c.quantity, title);
          // Corporate rows can arrive without a handle (a bundle item whose
          // product isn't published) and must not offer a dead affordance.
          const clickable = this.hostDriven ? Boolean(handle) : true;
          const safeAlt = String(alt).replace(/"/g, '&quot;');

          return (
            `<li class="bundle-contents__item${clickable ? '' : ' bundle-contents__item--static'}"` +
            `${clickable ? ` data-handle="${handle}" role="button" tabindex="0"` : ''}>` +
            `${clickable ? `<span class="patrol-sr-only">View ${title}</span>` : ''}` +
            `${
              imgUrl
                ? `<div class="bundle-contents__img"><img src="${imgUrl}${
                    imgUrl.indexOf('?') === -1 ? '?' : '&'
                  }width=120" alt="${safeAlt}" width="60" height="60" loading="${
                    this.hostDriven ? 'eager' : 'lazy'
                  }"></div>`
                : ''
            }` +
            `<div class="bundle-contents__details">` +
            `<span class="bundle-contents__text"><span class="bundle-contents__qty">${row.qty}</span><span class="bundle-contents__name">${row.name}</span></span>` +
            `${subtitle ? `<span class="bundle-contents__option">${subtitle}</span>` : ''}` +
            `</div>` +
            `${
              clickable
                ? `<svg class="bundle-contents__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>`
                : ''
            }` +
            `</li>`
          );
        })
        .join('');

      this.setVisible(true);
    }
  }

  customElements.define('bundle-contents', BundleContents);
})();
